/* ============================================================
   Custom client-side wishlist
   - localStorage-backed (key: hatsy_wishlist)
   - v2 data shape: { v: 2, boards: [ { id, name, createdAt, items: [] } ] }
     A v1 install (a flat array of saved products) is migrated in place,
     on first read, into a single default board so nobody's saves are lost.
   - Toggles the existing .wk-selected class on .js-product-wishlist
     elements (so theme aesthetic is fully preserved)
   - Listens to a custom 'wishlist:updated' event so all hearts and
     the nav counter stay in sync
   - Renders inside the drawer when opened. Defaults to a flattened,
     most-recent-first view across every board, but the drawer now has its
     own lightweight board picker (added later than the rest of this file)
     letting a shopper scope the list to one board, add new hearts straight
     into that board, and copy that board's share link -- without leaving
     the drawer. The full wishlist page remains the richer place to
     create/rename/delete boards and use the full share sheet.
   - window.EasyClothesWishlist exposes the board CRUD API for that page
     and for the share sheet, built separately
   ============================================================ */
(function () {
    var KEY = 'hatsy_wishlist';
    var DEFAULT_BOARD_NAME = 'My Wishlist';

    /* Which board the drawer's item list is currently scoped to. null/''
       means "All items" (the original flattened view). Session-only, not
       persisted -- picking a board in the drawer is a lightweight viewing/
       adding preference for this page load, not a permanent setting.
       Selecting a specific board also changes where a new heart click
       saves to (see toggle() below) and unlocks the drawer's Share button. */
    var drawerSelectedBoardId = null;

    function uid() {
        return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    }

    function blankStore() {
        return { v: 2, boards: [{ id: uid(), name: DEFAULT_BOARD_NAME, createdAt: Date.now(), items: [] }] };
    }

    function freshStore() {
        /* blankStore() alone only ever lives in memory — every caller
           (getBoard, getBoards, renderIndex, renderDetail, ...) that reads
           via getStore() needs to see the SAME board id back on the next
           call, not a new randomly-generated one. So any time we fall back
           to a blank store, persist it immediately (silently) before
           handing it back. Skipping this made a page like the board index
           regenerate a different default-board id on every single read —
           harmless for the drawer's flat item list, but it meant clicking
           into "My Wishlist" right after landing on the page (before any
           heart had ever been tapped, so nothing had saveStore()'d yet)
           opened a board id that no longer matched what getBoard() saw an
           instant later, and silently bounced back to the index. */
        var store = blankStore();
        saveStore(store, true);
        return store;
    }

    function getStore() {
        var raw;
        try {
            raw = localStorage.getItem(KEY);
        } catch (e) {
            return blankStore();
        }
        if (!raw) return freshStore();

        var parsed;
        try {
            parsed = JSON.parse(raw);
        } catch (e) {
            return freshStore();
        }

        if (Array.isArray(parsed)) {
            /* v1: a flat array of saved products. Migrate into a single
               default board and persist the upgraded shape immediately,
               so this branch only ever runs once per shopper. */
            var migrated = blankStore();
            migrated.boards[0].items = parsed.map(function (p) {
                return Object.assign({ addedAt: Date.now(), note: '' }, p);
            });
            saveStore(migrated, true);
            return migrated;
        }

        if (parsed && parsed.v === 2 && Array.isArray(parsed.boards) && parsed.boards.length) {
            return parsed;
        }

        /* Unrecognized or corrupt value — start clean rather than get stuck
           unable to save. */
        return freshStore();
    }

    function saveStore(store, silent) {
        try {
            localStorage.setItem(KEY, JSON.stringify(store));
        } catch (e) {
            // Storage might be full or disabled; fail silently
        }
        if (!silent) {
            document.dispatchEvent(new CustomEvent('wishlist:updated'));
        }
    }

    function findItemLocation(store, productId) {
        for (var i = 0; i < store.boards.length; i++) {
            var idx = store.boards[i].items.findIndex(function (p) { return String(p.id) === String(productId); });
            if (idx > -1) return { board: store.boards[i], index: idx };
        }
        return null;
    }

    /* All items across every board, most-recently-added first. This is
       what the drawer's quick "recently saved" peek and the nav counter
       read — they aren't board-aware, by design; browsing and moving
       items between boards happens on the full wishlist page. */
    function getAllItemsFlat() {
        var store = getStore();
        var all = [];
        store.boards.forEach(function (b) {
            b.items.forEach(function (p) { all.push(p); });
        });
        all.sort(function (a, b) { return (b.addedAt || 0) - (a.addedAt || 0); });
        return all;
    }

    function getTotalCount() {
        return getStore().boards.reduce(function (n, b) { return n + b.items.length; }, 0);
    }

    function getBoards() {
        return getStore().boards;
    }

    function getBoard(boardId) {
        return getStore().boards.find(function (b) { return b.id === boardId; }) || null;
    }

    function getDefaultBoardId() {
        return getStore().boards[0].id;
    }

    function createBoard(name) {
        var trimmed = (name || '').trim();
        if (!trimmed) return null;
        var store = getStore();
        var board = { id: uid(), name: trimmed, createdAt: Date.now(), items: [] };
        store.boards.push(board);
        saveStore(store);
        return board.id;
    }

    function renameBoard(boardId, name) {
        var trimmed = (name || '').trim();
        if (!trimmed) return;
        var store = getStore();
        var board = store.boards.find(function (b) { return b.id === boardId; });
        if (!board) return;
        board.name = trimmed;
        saveStore(store);
    }

    function deleteBoard(boardId) {
        var store = getStore();
        if (store.boards.length <= 1) return; // always keep at least one board
        store.boards = store.boards.filter(function (b) { return b.id !== boardId; });
        saveStore(store);
    }

    function moveItem(productId, toBoardId) {
        var store = getStore();
        var loc = findItemLocation(store, productId);
        var target = store.boards.find(function (b) { return b.id === toBoardId; });
        if (!loc || !target || loc.board.id === toBoardId) return;
        var already = target.items.some(function (p) { return String(p.id) === String(productId); });
        var item = loc.board.items.splice(loc.index, 1)[0];
        if (!already) target.items.push(item);
        saveStore(store);
    }

    /* Adds a fully-formed item straight into a specific board, skipping the
       "default board" logic that toggle() uses. This is for the "Save this
       board" action on the shared-link recipient view (step 4) — the items
       being added were re-fetched live from the storefront, not clicked
       from a heart button, so there's no getProductFromButton() call to
       reuse. Silently no-ops on a missing board or a duplicate id, same
       policy as the rest of this file. */
    function addItemToBoard(boardId, item) {
        if (!item || !item.id) return false;
        var store = getStore();
        var board = store.boards.find(function (b) { return b.id === boardId; });
        if (!board) return false;
        var already = board.items.some(function (p) { return String(p.id) === String(item.id); });
        if (!already) {
            item.addedAt = item.addedAt || Date.now();
            item.note = item.note || '';
            board.items.push(item);
        }
        saveStore(store);
        return true;
    }

    function setItemNote(productId, note) {
        var store = getStore();
        var loc = findItemLocation(store, productId);
        if (!loc) return;
        loc.board.items[loc.index].note = note;
        /* Silent save: this fires on every keystroke (the note field's
           'input' handler calls this directly, with no debounce). A
           non-silent save dispatches 'wishlist:updated', which the board
           page listens to and responds to by fully re-rendering the item
           grid (renderDetail() replaces detailItems.innerHTML) — that
           destroys and recreates the note <input> element on every single
           keystroke, which drops focus and forces a re-click before each
           next letter can be typed. Nothing needs to re-render on a note
           edit (the input already shows what was typed; no other UI
           reflects note text), so this save skips the event entirely. */
        saveStore(store, true);
    }

    function removeItem(productId) {
        var store = getStore();
        var loc = findItemLocation(store, productId);
        if (!loc) return;
        loc.board.items.splice(loc.index, 1);
        saveStore(store);
    }

    function clearAll() {
        var store = getStore();
        store.boards.forEach(function (b) { b.items = []; });
        saveStore(store);
    }

    function getProductFromButton(btn) {
        return {
            id: btn.dataset.productId || btn.dataset.variantId || btn.dataset.productHandle || '',
            variantId: btn.dataset.variantId || '',
            variantTitle: btn.dataset.variantTitle || '',
            title: btn.dataset.productTitle || '',
            price: btn.dataset.productPrice || '',
            image: btn.dataset.productImage || '',
            url: btn.dataset.productUrl || '#'
        };
    }

    /* Heart click: adds to the default (first) board if the product isn't
       saved anywhere yet; removes it from wherever it currently lives if
       it is. This preserves the existing one-tap save behavior — board
       assignment happens later, from the board page's move menu, not at
       save time -- UNLESS a shopper has picked a specific board in the
       drawer's own board picker this page load, in which case new hearts
       go straight into that board instead. Opt-in: nothing changes for a
       shopper who's never touched the picker. */
    function toggle(product) {
        if (!product.id) return false;
        var store = getStore();
        var loc = findItemLocation(store, product.id);
        if (loc) {
            loc.board.items.splice(loc.index, 1);
            saveStore(store);
            return false;
        }
        product.addedAt = Date.now();
        product.note = product.note || '';
        var targetBoard = (drawerSelectedBoardId && store.boards.find(function (b) { return b.id === drawerSelectedBoardId; })) || store.boards[0];
        targetBoard.items.push(product);
        saveStore(store);
        return true;
    }

    function isInWishlist(id) {
        if (!id) return false;
        return !!findItemLocation(getStore(), id);
    }

    function syncAllHearts() {
        var btns = document.querySelectorAll('.js-product-wishlist');
        btns.forEach(function (btn) {
            var id = btn.dataset.productId || btn.dataset.variantId || btn.dataset.productHandle;
            btn.classList.toggle('wk-selected', isInWishlist(id));
        });
    }

    function updateNavCounter() {
        var count = getTotalCount();
        var bubbles = document.querySelectorAll('.js-wishlist-count-bubble');
        bubbles.forEach(function (b) {
            b.textContent = count;
            if (count > 0) {
                b.classList.remove('u-none');
            } else {
                b.classList.add('u-none');
            }
        });
        var headerCount = document.querySelector('.js-drawer-wishlist-count');
        if (headerCount) headerCount.textContent = '(' + count + ')';
        /* Update the subtitle text in the new header design */
        var countText = document.querySelector('.js-drawer-wishlist-count-text');
        if (countText) {
            if (count === 0) countText.textContent = 'No selections yet';
            else if (count === 1) countText.textContent = '1 piece curated';
            else countText.textContent = count + ' pieces curated';
        }
        /* Show/hide footer based on count */
        var footer = document.querySelector('.js-wishlist-footer');
        if (footer) {
            if (count > 0) footer.removeAttribute('hidden');
            else footer.setAttribute('hidden', '');
        }
        /* Show/hide the "share your wishlist" nudge based on count, same
           rule as the footer above — no point suggesting someone share an
           empty wishlist. */
        var shareNudge = document.querySelector('.js-wishlist-share-nudge');
        if (shareNudge) {
            if (count > 0) shareNudge.removeAttribute('hidden');
            else shareNudge.setAttribute('hidden', '');
        }
        /* Keep the board picker's own visibility/options current even when
           the drawer isn't open right now (mirrors the footer/nudge above),
           so it's already correct the moment the drawer next opens. */
        renderBoardPicker();
    }

    function escapeHtml(s) {
        if (!s) return '';
        return String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    function emptyStateHtml() {
        return '' +
            '<div class="drawer-wishlist__empty-state">' +
                '<div class="drawer-wishlist__empty-illustration" aria-hidden="true">' +
                    '<svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">' +
                        '<circle cx="60" cy="60" r="58" stroke="currentColor" stroke-width="0.5" stroke-dasharray="2 3" opacity="0.3"/>' +
                        '<circle cx="60" cy="60" r="42" stroke="currentColor" stroke-width="0.5" opacity="0.2"/>' +
                        '<path d="M60 84s-22-13.6-22-28c0-7.7 5.6-13 12.5-13 4 0 7 2 9.5 5 2.5-3 5.5-5 9.5-5 6.9 0 12.5 5.3 12.5 13 0 14.4-22 28-22 28z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>' +
                        '<circle cx="60" cy="60" r="2" fill="currentColor" opacity="0.4"/>' +
                    '</svg>' +
                '</div>' +
                '<p class="drawer-wishlist__empty-title">Your wishlist awaits</p>' +
                '<p class="drawer-wishlist__empty-text">Save your favorite pieces by tapping the heart icon on any product. Curate your dream wardrobe here.</p>' +
                '<a href="/collections/all-new" class="drawer-wishlist__empty-cta js-drawer-close">' +
                    '<span>Discover what\'s new</span>' +
                    '<svg width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true">' +
                        '<path d="M1 5h12m0 0L9 1m4 4L9 9" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
                    '</svg>' +
                '</a>' +
            '</div>';
    }

    function wishlistPageBaseUrl() {
        /* The drawer renders globally on every page (via layout/theme.liquid),
           not just the wishlist page itself, so a share link built from here
           can't assume window.location is already the wishlist page the way
           component-wishlist-boards.js's own buildShareLink() safely can (it
           only ever runs ON that page). The "View all boards" link (always
           present in the drawer's static markup — see
           sections/drawer-wishlist.liquid) already carries the correct
           Liquid-resolved wishlist page URL, so read it from there instead
           of hardcoding a path here. */
        var link = document.querySelector('.drawer-wishlist__view-all-link');
        if (link) {
            try {
                return new URL(link.href, window.location.origin).pathname;
            } catch (e) {}
        }
        return '/pages/wishlist';
    }

    function b64UrlEncode(str) {
        var b64 = btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function (_, hex) {
            return String.fromCharCode('0x' + hex);
        }));
        return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }

    /* Same payload shape as component-wishlist-boards.js's own
       buildShareLink() (board name + [url, variantId, note] tuples) --
       kept as a small, independent duplicate rather than a shared import,
       since this file loads globally on every page and that one only
       loads on the wishlist page itself. */
    function buildShareLinkForBoard(board) {
        var payload = {
            n: board.name,
            i: board.items.map(function (it) {
                return [it.url, it.variantId || it.id, it.note || ''];
            })
        };
        var encoded = b64UrlEncode(JSON.stringify(payload));
        var base = window.location.origin + wishlistPageBaseUrl();
        return base + '?view=wishlist-boards&w=' + encoded;
    }

    function fallbackCopyText(text) {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        try {
            document.execCommand('copy');
        } catch (e) {}
        document.body.removeChild(ta);
    }

    function copyToClipboard(text) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            return navigator.clipboard.writeText(text).catch(function () {
                fallbackCopyText(text);
            });
        }
        fallbackCopyText(text);
        return Promise.resolve();
    }

    /* Resolves which board the Share button should act on for the drawer's
       CURRENT scope. When a specific board is picked, that's unambiguous.
       When the picker is on "All items" (drawerSelectedBoardId is null),
       sharing is normally left alone -- with several boards there's no one
       board "All items" could mean. But the common case is a shopper with
       just ONE board total, and for them "All items" and that board are the
       exact same list -- there's nothing ambiguous about it. Without this,
       Share only ever appeared after manually picking that one board from a
       dropdown that, with nothing else to pick, looked pointless to touch --
       so Share read as permanently missing for most shoppers. Used by both
       renderBoardPicker() (to show/hide the button) and the click handler
       (to know what to actually share). */
    function resolveShareBoard(boards) {
        if (drawerSelectedBoardId) return getBoard(drawerSelectedBoardId);
        return boards.length === 1 ? boards[0] : null;
    }

    /* Populates the drawer's board-picker <select> from the current boards,
       keeps it in sync if the previously-picked board no longer exists
       (e.g. deleted from the full wishlist page in another tab), and shows/
       hides the picker and its Share button. Called both from
       updateNavCounter() (so it's already correct before the drawer next
       opens) and from renderDrawer() (so it's current the moment the item
       list itself re-renders). */
    function renderBoardPicker() {
        var picker = document.querySelector('.js-wishlist-board-picker');
        var select = document.querySelector('.js-wishlist-board-select');
        var shareBtn = document.querySelector('.js-wishlist-share-board');
        if (!picker || !select) return;

        var boards = getBoards();
        if (drawerSelectedBoardId && !boards.some(function (b) { return b.id === drawerSelectedBoardId; })) {
            drawerSelectedBoardId = null;
        }

        var optionsHtml = '<option value="">All items</option>';
        boards.forEach(function (b) {
            optionsHtml += '<option value="' + escapeHtml(b.id) + '">' + escapeHtml(b.name) + '</option>';
        });
        select.innerHTML = optionsHtml;
        select.value = drawerSelectedBoardId || '';

        picker.hidden = getTotalCount() === 0;

        if (shareBtn) {
            var shareableBoard = resolveShareBoard(boards);
            shareBtn.hidden = !shareableBoard || !shareableBoard.items.length;
        }
    }

    function renderDrawer() {
        renderBoardPicker();
        var body = document.getElementById('js-drawer-wishlist-body');
        if (!body) return;
        var boards = getBoards();
        var scopedBoard = drawerSelectedBoardId ? getBoard(drawerSelectedBoardId) : null;
        /* Each entry keeps track of which board the item currently lives in --
           getAllItemsFlat() alone loses that association once boards are
           flattened together, and the new per-item "move to board" control
           below needs to know it (to pre-select the item's current board and
           to know where NOT to bother moving it). */
        var entries;
        if (scopedBoard) {
            entries = scopedBoard.items.map(function (p) { return { item: p, boardId: scopedBoard.id }; });
        } else {
            entries = [];
            boards.forEach(function (b) {
                b.items.forEach(function (p) { entries.push({ item: p, boardId: b.id }); });
            });
        }
        entries.sort(function (a, b) { return (b.item.addedAt || 0) - (a.item.addedAt || 0); });
        if (!entries.length) {
            body.innerHTML = emptyStateHtml();
            return;
        }
        /* Only worth offering a "move to board" control when there's actually
           somewhere else to move an item TO. */
        var showBoardMove = boards.length > 1;
        var html = '<ul class="drawer-wishlist__list">';
        entries.forEach(function (entry) {
            var p = entry.item;
            var variantId = p.variantId || p.id;
            var boardMoveHtml = '';
            if (showBoardMove) {
                var boardOptionsHtml = '';
                boards.forEach(function (b) {
                    boardOptionsHtml += '<option value="' + escapeHtml(b.id) + '"' +
                        (b.id === entry.boardId ? ' selected' : '') + '>' + escapeHtml(b.name) + '</option>';
                });
                boardMoveHtml = '' +
                    '<div class="drawer-wishlist__item-board">' +
                        '<span class="drawer-wishlist__item-board-label">Move to</span>' +
                        '<select class="drawer-wishlist__item-board-select js-wishlist-item-board-select" data-id="' + escapeHtml(p.id) + '" aria-label="Move ' + escapeHtml(p.title) + ' to board">' +
                            boardOptionsHtml +
                        '</select>' +
                    '</div>';
            }
            html += '' +
                '<li class="drawer-wishlist__item" data-id="' + escapeHtml(p.id) + '">' +
                    '<a href="' + escapeHtml(p.url) + '" class="drawer-wishlist__item-image" aria-label="' + escapeHtml(p.title) + '">' +
                        (p.image ? '<img src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '') +
                        '<span class="drawer-wishlist__item-image-overlay" aria-hidden="true">' +
                            '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>' +
                        '</span>' +
                    '</a>' +
                    '<div class="drawer-wishlist__item-body">' +
                        '<div class="drawer-wishlist__item-info">' +
                            '<a href="' + escapeHtml(p.url) + '" class="drawer-wishlist__item-title">' + escapeHtml(p.title) + '</a>' +
                            (p.variantTitle && p.variantTitle.toLowerCase() !== 'default title'
                                ? '<span class="drawer-wishlist__item-size">Size ' + escapeHtml(p.variantTitle) + '</span>'
                                : '') +
                            '<div class="drawer-wishlist__item-meta">' +
                                '<span class="drawer-wishlist__item-price">' + p.price + '</span>' +
                            '</div>' +
                            boardMoveHtml +
                        '</div>' +
                        '<div class="drawer-wishlist__item-actions">' +
                            '<button type="button" class="drawer-wishlist__btn drawer-wishlist__btn--add js-wishlist-add-to-cart" data-id="' + escapeHtml(p.id) + '" data-variant-id="' + escapeHtml(variantId) + '" data-product-url="' + escapeHtml(p.url) + '">' +
                                '<span class="drawer-wishlist__btn-label">Add to cart</span>' +
                                '<svg class="drawer-wishlist__btn-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>' +
                                '<span class="drawer-wishlist__btn-spinner"></span>' +
                            '</button>' +
                            '<button type="button" class="drawer-wishlist__btn drawer-wishlist__btn--remove js-wishlist-remove" data-id="' + escapeHtml(p.id) + '" aria-label="Remove from wishlist">' +
                                '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
                            '</button>' +
                        '</div>' +
                    '</div>' +
                '</li>';
        });
        html += '</ul>';
        body.innerHTML = html;
    }

    /* ============================================================
       SIZE PICKER SUPPORT
       Fetches Shopify product JSON and determines whether the
       product needs a size selection before adding to cart.
       ============================================================ */
    var productDataCache = {};
    function fetchProductData(productUrl) {
        if (!productUrl) return Promise.reject(new Error('no url'));
        /* Strip query/hash for cache key and JSON endpoint */
        var cleanUrl = productUrl.split('?')[0].split('#')[0];
        if (productDataCache[cleanUrl]) {
            return Promise.resolve(productDataCache[cleanUrl]);
        }
        return fetch(cleanUrl + '.js', { headers: { 'Accept': 'application/json' } })
            .then(function (r) {
                if (!r.ok) throw new Error('product fetch failed');
                return r.json();
            })
            .then(function (data) {
                productDataCache[cleanUrl] = data;
                return data;
            });
    }
    function getSizeOptions(product) {
        var result = { hasSizes: false, sizeIndex: -1, options: [] };
        if (!product || !product.variants || !product.variants.length) return result;
        /* Single variant or only variant is "Default Title" — no sizing. */
        if (product.variants.length === 1) {
            var only = product.variants[0];
            var t = (only.title || '').toLowerCase();
            if (t === '' || t === 'default title') return result;
        }
        /* Find the option whose name contains "size" or "taille" */
        var optionsArr = product.options || [];
        var sizeIndex = -1;
        for (var i = 0; i < optionsArr.length; i++) {
            var optName = (typeof optionsArr[i] === 'string' ? optionsArr[i] : (optionsArr[i].name || ''));
            var lower = optName.toLowerCase();
            if (lower.indexOf('size') !== -1 || lower.indexOf('taille') !== -1) {
                sizeIndex = i;
                break;
            }
        }
        /* If product has multiple variants but no option literally named size,
           still treat variant.title as the size selector (since the variant
           title differs). */
        if (sizeIndex === -1) {
            if (product.variants.length > 1) {
                /* Use variant.title as the label */
                result.hasSizes = true;
                result.sizeIndex = -1;
                result.options = product.variants.map(function (v) {
                    return {
                        label: v.title,
                        variantId: v.id,
                        available: v.available
                    };
                });
            }
            return result;
        }
        /* We have a real size option */
        result.hasSizes = true;
        result.sizeIndex = sizeIndex;
        result.options = product.variants.map(function (v) {
            var label = (v.options && v.options[sizeIndex]) || v.title;
            return {
                label: label,
                variantId: v.id,
                available: v.available
            };
        });
        /* Dedupe by label — for products where size is one axis among
           several (e.g. color + size), we only want one button per size. */
        var seen = {};
        result.options = result.options.filter(function (o) {
            if (seen[o.label]) {
                /* If we already saw this label, prefer the available one */
                if (o.available && !seen[o.label].available) {
                    seen[o.label].variantId = o.variantId;
                    seen[o.label].available = true;
                }
                return false;
            }
            seen[o.label] = o;
            return true;
        });
        return result;
    }
    function renderSizePicker(itemEl, sizeInfo, idToRemove, productUrl) {
        if (!itemEl) return;
        var actions = itemEl.querySelector('.drawer-wishlist__item-actions');
        if (!actions) return;
        var pickerHtml = '<div class="drawer-wishlist__size-picker">';
        sizeInfo.options.forEach(function (opt) {
            var disabled = opt.available ? '' : 'disabled aria-disabled="true"';
            pickerHtml += '<button type="button" class="drawer-wishlist__btn drawer-wishlist__btn--size js-wishlist-size-pick' +
                (opt.available ? '' : ' drawer-wishlist__btn--size-unavailable') +
                '" data-variant-id="' + escapeHtml(String(opt.variantId)) +
                '" data-product-url="' + escapeHtml(productUrl) +
                '" data-id="' + escapeHtml(idToRemove) +
                '" ' + disabled + '>' +
                '<span>' + escapeHtml(opt.label) + '</span>' +
                '</button>';
        });
        pickerHtml += '<button type="button" class="drawer-wishlist__btn drawer-wishlist__btn--remove js-wishlist-remove" data-id="' + escapeHtml(idToRemove) + '" aria-label="Remove from wishlist">' +
            '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
            '</button>';
        pickerHtml += '</div>';
        actions.outerHTML = pickerHtml;
    }
    function addAndRemove(variantId, productUrl, btn, idToRemove) {
        return addToCart(variantId, productUrl, btn).then(function () {
            removeItem(idToRemove);
            renderDrawer();
        });
    }

    function addToCart(variantId, productUrl, btn) {
        if (!variantId) return Promise.resolve();
        if (btn) btn.classList.add('drawer-wishlist__btn--loading');
        return fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                items: [{ id: parseInt(variantId, 10) || variantId, quantity: 1 }]
            })
        })
        .then(function (r) {
            if (!r.ok) {
                // Variant ID may be a product handle or product id (from older saves).
                // Fetch the product JSON and use the first available variant.
                if (productUrl) {
                    return fetch(productUrl + '.js').then(function (rr) {
                        if (!rr.ok) throw new Error('product fetch failed');
                        return rr.json();
                    }).then(function (product) {
                        var v = (product.variants || []).find(function (x) { return x.available; });
                        if (!v) throw new Error('no available variant');
                        return fetch('/cart/add.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                            body: JSON.stringify({ items: [{ id: v.id, quantity: 1 }] })
                        });
                    });
                }
                throw new Error('add to cart failed');
            }
            return r;
        })
        .then(function () {
            if (btn) btn.classList.remove('drawer-wishlist__btn--loading');
            // Refresh cart UI
            document.dispatchEvent(new CustomEvent('cart:updated'));
            if (window.w3 && window.w3.cart && window.w3.cart.update) {
                window.w3.cart.update();
            }
            // The theme's w3-cart custom element typically refreshes via Shopify's section render
            return fetch('/?sections=drawer-cart').then(function (r) {
                if (r.ok) return r.json();
            }).then(function (sections) {
                if (sections && sections['drawer-cart']) {
                    var oldDrawer = document.querySelector('w3-drawer-cart');
                    if (oldDrawer) {
                        var tmp = document.createElement('div');
                        tmp.innerHTML = sections['drawer-cart'];
                        var newDrawer = tmp.querySelector('w3-drawer-cart');
                        if (newDrawer) {
                            // Just update the cart bubble count and inner cart-products
                            var newCount = tmp.querySelector('.cart-icon-bubble__count');
                            var oldCounts = document.querySelectorAll('.cart-icon-bubble__count');
                            if (newCount) {
                                oldCounts.forEach(function (el) { el.textContent = newCount.textContent; });
                            }
                            var newCartProducts = newDrawer.querySelector('#cart-products');
                            var oldCartProducts = oldDrawer.querySelector('#cart-products');
                            if (newCartProducts && oldCartProducts) {
                                oldCartProducts.replaceWith(newCartProducts);
                            }
                            // Update free-shipping bar
                            var newShipping = newDrawer.querySelector('.cart-free-shipping');
                            var oldShipping = oldDrawer.querySelector('.cart-free-shipping');
                            if (newShipping && oldShipping) {
                                oldShipping.replaceWith(newShipping);
                            }
                            // Update checkout button (price changes)
                            var newCheckout = newDrawer.querySelector('.cart-checkout');
                            var oldCheckout = oldDrawer.querySelector('.cart-checkout');
                            if (newCheckout && oldCheckout) {
                                oldCheckout.replaceWith(newCheckout);
                            }
                        }
                    }
                }
            });
        })
        .catch(function () {
            if (btn) btn.classList.remove('drawer-wishlist__btn--loading');
        });
    }

    // Click handler for any heart button
    document.addEventListener('click', function (e) {
        var heart = e.target.closest('.js-product-wishlist');
        if (heart) {
            e.preventDefault();
            e.stopPropagation();
            var product = getProductFromButton(heart);
            toggle(product);
            return;
        }
        // Add-to-cart from inside drawer: check if size selection is needed
        var addBtn = e.target.closest('.js-wishlist-add-to-cart');
        if (addBtn) {
            e.preventDefault();
            if (addBtn.classList.contains('drawer-wishlist__btn--loading')) return;
            var itemEl = addBtn.closest('.drawer-wishlist__item');
            var idToRemove = addBtn.dataset.id;
            var productUrl = addBtn.dataset.productUrl;
            var variantId = addBtn.dataset.variantId;
            /* Fetch product JSON to find out if size choice is required. */
            addBtn.classList.add('drawer-wishlist__btn--loading');
            fetchProductData(productUrl).then(function (product) {
                var sizeInfo = getSizeOptions(product);
                if (!sizeInfo.hasSizes) {
                    /* No sizes needed — add the first available variant directly. */
                    var firstVariant = (product && product.variants || []).find(function (v) { return v.available; }) || (product && product.variants && product.variants[0]);
                    var idToAdd = (firstVariant && firstVariant.id) || variantId;
                    addBtn.classList.remove('drawer-wishlist__btn--loading');
                    return addAndRemove(idToAdd, productUrl, addBtn, idToRemove);
                }
                /* Multiple sizes — render the size picker inline. */
                addBtn.classList.remove('drawer-wishlist__btn--loading');
                renderSizePicker(itemEl, sizeInfo, idToRemove, productUrl);
            }).catch(function () {
                /* Fallback: try the saved variantId */
                addBtn.classList.remove('drawer-wishlist__btn--loading');
                addAndRemove(variantId, productUrl, addBtn, idToRemove);
            });
            return;
        }
        /* Size button inside the inline size picker */
        var sizeBtn = e.target.closest('.js-wishlist-size-pick');
        if (sizeBtn) {
            e.preventDefault();
            if (sizeBtn.classList.contains('drawer-wishlist__btn--loading')) return;
            if (sizeBtn.disabled || sizeBtn.getAttribute('aria-disabled') === 'true') return;
            var vId = sizeBtn.dataset.variantId;
            var pUrl = sizeBtn.dataset.productUrl;
            var rId = sizeBtn.dataset.id;
            sizeBtn.classList.add('drawer-wishlist__btn--loading');
            addAndRemove(vId, pUrl, sizeBtn, rId);
            return;
        }
        // Add ALL to cart from footer button
        var addAllBtn = e.target.closest('.js-wishlist-add-all');
        if (addAllBtn) {
            e.preventDefault();
            if (addAllBtn.classList.contains('drawer-wishlist__add-all--loading')) return;
            addAllBtn.classList.add('drawer-wishlist__add-all--loading');
            var allItems = getAllItemsFlat();
            if (!allItems.length) {
                addAllBtn.classList.remove('drawer-wishlist__add-all--loading');
                return;
            }
            /* Add sequentially so server doesn't get a race */
            var idx = 0;
            function next() {
                if (idx >= allItems.length) {
                    addAllBtn.classList.remove('drawer-wishlist__add-all--loading');
                    clearAll();
                    renderDrawer();
                    return;
                }
                var p = allItems[idx++];
                /* For "Add all", we use the saved variant or first-available;
                   we don't prompt for size since there's no UI for it here. */
                addToCart(p.variantId || p.id, p.url, null).then(next, next);
            }
            next();
            return;
        }
        // Remove button inside drawer
        var removeBtn = e.target.closest('.js-wishlist-remove');
        if (removeBtn) {
            e.preventDefault();
            removeItem(removeBtn.dataset.id);
            renderDrawer();
            return;
        }
        // Share button in the drawer's board picker -- copies that board's
        // share link, same payload shape/URL scheme as the full page's own
        // share sheet, just without leaving the drawer.
        var shareBoardBtn = e.target.closest('.js-wishlist-share-board');
        if (shareBoardBtn) {
            e.preventDefault();
            var boardToShare = resolveShareBoard(getBoards());
            if (!boardToShare || !boardToShare.items.length) return;
            var shareLink = buildShareLinkForBoard(boardToShare);
            var shareLabel = shareBoardBtn.querySelector('.js-wishlist-share-board-label');
            copyToClipboard(shareLink).then(function () {
                if (!shareLabel) return;
                var original = shareLabel.textContent;
                shareLabel.textContent = 'Copied!';
                setTimeout(function () { shareLabel.textContent = original; }, 1800);
            });
            return;
        }
        // Open-drawer button: render fresh just before drawer opens
        var openBtn = e.target.closest('.js-open-drawer-wishlist');
        if (openBtn) {
            renderDrawer();
        }
    });

    // Per-item "move to board" <select> on a drawer item card: reassigns
    // that one item to a different board without leaving the drawer.
    document.addEventListener('change', function (e) {
        var itemBoardSelect = e.target.closest('.js-wishlist-item-board-select');
        if (itemBoardSelect) {
            moveItem(itemBoardSelect.dataset.id, itemBoardSelect.value);
            renderDrawer();
            return;
        }
        // Board picker's <select>: switch the drawer's scope between "All
        // items" and one specific board.
        var boardSelect = e.target.closest('.js-wishlist-board-select');
        if (!boardSelect) return;
        drawerSelectedBoardId = boardSelect.value || null;
        renderDrawer();
    });

    // Sync on update
    document.addEventListener('wishlist:updated', function () {
        syncAllHearts();
        updateNavCounter();
        // If drawer is open, re-render it
        var drawerEl = document.querySelector('.js-drawer-wishlist');
        if (drawerEl && drawerEl.classList.contains('drawer--visible')) {
            renderDrawer();
        }
    });

    // Init on DOM ready
    function init() {
        syncAllHearts();
        updateNavCounter();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Re-sync when product cards are loaded dynamically (filters, load more, etc.)
    document.addEventListener('theme:filters:update', syncAllHearts);
    document.addEventListener('theme:search:update', syncAllHearts);
    document.addEventListener('product-recommendations:update', syncAllHearts);
    document.addEventListener('listing-load-more:loaded', syncAllHearts);

    /* Public boards API — consumed by the wishlist board page and the
       share sheet (built separately). Kept as one namespaced object
       rather than more globals. */
    window.EasyClothesWishlist = {
        getBoards: getBoards,
        getBoard: getBoard,
        getDefaultBoardId: getDefaultBoardId,
        getTotalCount: getTotalCount,
        getAllItemsFlat: getAllItemsFlat,
        createBoard: createBoard,
        renameBoard: renameBoard,
        deleteBoard: deleteBoard,
        moveItem: moveItem,
        addItemToBoard: addItemToBoard,
        setItemNote: setItemNote,
        removeItem: removeItem,
        isInWishlist: isInWishlist,
        fetchSizeOptions: function (productUrl) {
            return fetchProductData(productUrl).then(getSizeOptions);
        },
        /* Raw Shopify product JSON (cached), for the shared-link recipient
           view (step 4), which needs live title/price/image/variants — not
           just the size-picker shape fetchSizeOptions derives from it. */
        fetchProduct: fetchProductData,
        addVariantToCart: addToCart
    };
})();

/* ============================================================
   Model info drawer — swap photo + caption based on which model
   name was clicked. The clicked button now carries the model's
   src/name/caption as data attributes directly, so there's no
   coupling between the trigger button and the drawer's data.
   ============================================================ */
(function () {
    document.addEventListener('click', function (e) {
        var btn = e.target.closest('.js-open-drawer-model');
        if (!btn) return;
        var photo = document.querySelector('.js-drawer-model-photo');
        var nameEl = document.querySelector('.js-drawer-model-name');
        var captionEl = document.querySelector('.js-drawer-model-caption');
        if (!photo) return;
        /* Prefer data directly on the button. Falls back to the photo's
           data-model-N-* attributes for backward compatibility. */
        var src = btn.dataset.modelSrc;
        var name = btn.dataset.modelName;
        var caption = btn.dataset.modelCaption;
        if (!src) {
            var modelNum = btn.dataset.model || '1';
            src = photo.dataset['model' + modelNum + 'Src'];
            name = photo.dataset['model' + modelNum + 'Name'];
            caption = photo.dataset['model' + modelNum + 'Caption'];
        }
        if (src) photo.src = src;
        if (nameEl && name) nameEl.textContent = name;
        if (captionEl && caption) captionEl.textContent = caption;
    });
})();

/* ============================================================
   Reassurance carousel — auto-rotating dots and slides
   ============================================================ */
(function () {
    function initCarousel(el) {
        var slides = el.querySelectorAll('.product__reassurance-slide');
        var dots = el.querySelectorAll('.product__reassurance-dot');
        if (!slides.length) return;
        var current = 0;

        function show(idx) {
            slides.forEach(function (s, i) {
                s.classList.toggle('product__reassurance-slide--active', i === idx);
            });
            dots.forEach(function (d, i) {
                d.classList.toggle('product__reassurance-dot--active', i === idx);
            });
            current = idx;
        }

        var interval = setInterval(function () {
            show((current + 1) % slides.length);
        }, 2800);

        dots.forEach(function (d) {
            d.addEventListener('click', function () {
                clearInterval(interval);
                show(parseInt(d.dataset.reassuranceDot, 10) || 0);
                interval = setInterval(function () {
                    show((current + 1) % slides.length);
                }, 2800);
            });
        });
    }

    function initAll() {
        document.querySelectorAll('[data-reassurance-carousel]').forEach(initCarousel);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAll);
    } else {
        initAll();
    }
})();

/* ============================================================
   Out-of-stock "Create an Alert" button — delegates to Stoq's
   Restock Rocket button. The Stoq container is hidden by
   component-stock-alert.js but kept in the DOM so we can click it.

   Fix 2026-10-01 (intermittently dead button): when a shopper
   switches to a sold-out size, the theme re-renders the product
   form and wipes out the Restock Rocket button, and Restock Rocket
   never re-injects it -- so the old "find it and click it" had
   nothing to click. It also missed if clicked before Restock
   Rocket's async script had loaded. Now, if the button is missing,
   we ask Restock Rocket to re-render it for the selected variant
   and wait briefly for it (and for the app itself to load) before
   clicking, then keep its container hidden so no duplicate
   "Notify me" button shows.
   ============================================================ */
(function () {
    var SELECTORS = [
        '.restock-rocket-button-container button',
        '.restock-rocket-button-container a',
        '.restock-rocket-button',
        '[data-restock-rocket]',
        '[class*="back-in-stock"] button',
        '[class*="notify-me"] button',
        '[id*="restock-rocket"] button',
        '[id*="restock-rocket"] a',
        '.stoq-button',
        '#stoq-button',
        'button[class*="restock"]'
    ];
    var HIDDEN_CSS = 'display:none;visibility:hidden;position:absolute;left:-9999px;';
    var busy = false;

    function findStoqButton(scope) {
        // Prefer a button inside this product's own form, so a product
        // card elsewhere on the page can never be picked by mistake.
        var roots = scope ? [scope, document] : [document];
        for (var r = 0; r < roots.length; r++) {
            for (var i = 0; i < SELECTORS.length; i++) {
                var list = roots[r].querySelectorAll(SELECTORS[i]);
                for (var k = 0; k < list.length; k++) {
                    var el = list[k];
                    if (el.closest('.product-form__create-alert-btn')) continue;
                    if (roots[r] === document && el.closest('.product-card')) continue;
                    return el;
                }
            }
        }
        return null;
    }

    function hideContainer(el) {
        var container = el.closest('.restock-rocket-button-container') || el;
        container.style.cssText = HIDDEN_CSS;
        container.setAttribute('aria-hidden', 'true');
    }

    function currentVariantId(form) {
        var input = (form && form.querySelector('[name="id"]')) || document.querySelector('form[action*="/cart/add"] [name="id"]');
        var id = input ? Number(input.value) : 0;
        return id || null;
    }

    function askRestockRocketToRender(form) {
        var rr = window._RestockRocket;
        var id = currentVariantId(form);
        if (rr && typeof rr.renderButtonForVariant === 'function' && id) {
            try { rr.renderButtonForVariant(id); } catch (err) {}
            return true;
        }
        return false;
    }

    function clickStoq(stoq) {
        // Temporarily reveal it so the click triggers properly
        // (some apps check element visibility before opening)
        var container = stoq.closest('.restock-rocket-button-container') || stoq;
        container.style.cssText = 'position:absolute;left:0;top:0;opacity:0;pointer-events:auto;';
        try {
            stoq.click();
        } catch (err) {}
        // Always return it to hidden (a re-rendered container starts out
        // visible, so restoring its "original" style would show a duplicate)
        setTimeout(function () { hideContainer(stoq); }, 100);
    }

    document.addEventListener('click', function (e) {
        var btn = e.target.closest('.product-form__create-alert-btn');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        if (busy) return;

        var form = btn.closest('form');
        var stoq = findStoqButton(form);
        if (stoq) {
            clickStoq(stoq);
            return;
        }

        // Missing: re-render it (or wait for Restock Rocket to finish loading).
        busy = true;
        btn.setAttribute('aria-busy', 'true');
        var asked = askRestockRocketToRender(form);
        var tries = 0;
        var poll = setInterval(function () {
            tries++;
            var found = findStoqButton(form);
            if (found) {
                clearInterval(poll);
                busy = false;
                btn.removeAttribute('aria-busy');
                clickStoq(found);
                return;
            }
            // App script may have loaded since the click -- ask once it's there.
            if (!asked) asked = askRestockRocketToRender(form);
            if (tries >= 40) { // ~4s
                clearInterval(poll);
                busy = false;
                btn.removeAttribute('aria-busy');
            }
        }, 100);
    });
})();

/* ============================================================
   Keep wishlist button's data-variant-title in sync with the
   currently-selected size on the product page. When user picks a
   size on the product page, variant-radios fires a 'change' event;
   we read the selected label text and update the heart's dataset.
   ============================================================ */
(function () {
    function syncProductPageVariantTitle() {
        var radiosEl = document.querySelector('variant-radios');
        if (!radiosEl) return;
        var heartBtn = document.querySelector('.product__wishlist-button.js-product-wishlist');
        if (!heartBtn) return;
        /* Find the currently-checked size input by looking for a fieldset
           whose legend/name mentions size, then reading the checked input
           value. Falls back to whichever radio is checked. */
        var fieldsets = radiosEl.querySelectorAll('fieldset');
        var sizeValue = '';
        for (var i = 0; i < fieldsets.length; i++) {
            var fs = fieldsets[i];
            var legendText = (fs.querySelector('legend') || {}).textContent || '';
            var nameAttr = (fs.querySelector('input') || {}).name || '';
            var label = (legendText + ' ' + nameAttr).toLowerCase();
            if (label.indexOf('size') === -1 && label.indexOf('taille') === -1) continue;
            var checked = fs.querySelector('input:checked');
            if (checked) {
                sizeValue = checked.value || '';
                break;
            }
        }
        /* Fallback: first checked radio of any kind */
        if (!sizeValue) {
            var anyChecked = radiosEl.querySelector('input:checked');
            if (anyChecked) sizeValue = anyChecked.value || '';
        }
        if (sizeValue && sizeValue.toLowerCase() !== 'default title') {
            heartBtn.dataset.variantTitle = sizeValue;
        }
    }
    /* Listen to variant-radios change events (bubbles) */
    document.addEventListener('change', function (e) {
        var radios = e.target.closest && e.target.closest('variant-radios');
        if (!radios) return;
        /* Wait a tick so variant-radios finishes updating internal state */
        setTimeout(syncProductPageVariantTitle, 0);
    });
    /* Run once on load to ensure the initial state is correct */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', syncProductPageVariantTitle);
    } else {
        syncProductPageVariantTitle();
    }
})();
